import { useRef, useEffect } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';

const credentials = [
  {
    icon: 'ri-music-line',
    title: 'Gospel Music Direction',
    description: 'Collaborated with countless gospel artists as a music and worship director, providing musical leadership for live performances and studio productions.',
  },
  {
    icon: 'ri-stage-line',
    title: 'Stage Plays & Live Productions',
    description: 'Extensive experience across stage plays, live productions, and musical direction, bringing a well-rounded presence and creative excellence to every project.',
  },
  {
    icon: 'ri-award-line',
    title: 'Arts Strategist & Director',
    description: 'Distinguished Creative Media Strategist recognized for leading and developing creatives across multiple artistic disciplines at the highest levels.',
  },
  {
    icon: 'ri-heart-line',
    title: 'Neurodivergent Advocate',
    description: 'Passionate about creating inclusive, structured environments where neurodivergent creatives are not only supported but celebrated.',
  },
  {
    icon: 'ri-home-heart-line',
    title: 'Faith & Family Values',
    description: 'A devoted family man who leads with strong values, integrity, and principle, shaping the culture and environment of Creative Institute from the ground up.',
  },
  {
    icon: 'ri-global-line',
    title: 'Multi-Platform Creative Leadership',
    description: 'Experience leading creative initiatives across live events, recording studios, educational platforms, and media productions at a professional level.',
  },
];

const founderJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  name: 'Meet the Founder — Jaquan Little | Creative Institute LLC',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/meet-the-founder`,
  mainEntity: {
    '@type': 'Person',
    name: 'Jaquan Little',
    jobTitle: 'Founder & Creative Director',
    worksFor: { '@type': 'EducationalOrganization', name: 'Creative Institute LLC' },
    description: 'World-renowned worship and arts director, distinguished Creative Media Strategist, and founder of Creative Institute LLC.',
    knowsAbout: ['Music Direction', 'Gospel Music', 'Arts Education', 'Neurodivergent Development', 'Creative Coaching', 'Stage Production'],
  },
};

function CredentialCard({ icon, title, description, delay, isVisible }: {
  icon: string; title: string; description: string; delay: string; isVisible: boolean;
}) {
  return (
    <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} ${delay} flex gap-5 p-6 rounded-xl bg-white border border-gray-100 card-lift group`}>
      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-purple/10 text-brand-purple flex-shrink-0 group-hover:bg-brand-purple group-hover:text-white transition-all duration-300">
        <i className={`${icon} text-xl`} />
      </div>
      <div>
        <h3 className="font-heading text-base font-bold text-brand-blue-dark mb-2">{title}</h3>
        <p className="font-body text-gray-500 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function HeroBlock() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.65;
    }
  }, []);

  const handleScroll = () => {
    window.scrollTo({ top: window.innerHeight * 0.75, behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-[75vh] flex items-end overflow-hidden">
      {/* ── Real Video Background ── */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        src="https://www.dropbox.com/scl/fi/y824cb7jyzmmfu2bzljok/Video-Mar-29-2026-6-47-27-PM.mov?rlkey=0muvm1svkh7y0m99szoyx9lt1&st=qbw5esp0&raw=1"
        className="absolute inset-0 w-full h-full object-cover object-center z-0"
      />

      {/* ── Dark cinematic overlay ── */}
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/80 via-black/45 to-black/30" />

      {/* ── Subtle color tint for brand feel ── */}
      <div className="absolute inset-0 z-10 bg-gradient-to-br from-[#1E3A8A]/30 via-transparent to-[#5B21B6]/20" />

      {/* ── Ambient accent ── */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-brand-yellow/5 rounded-full blur-3xl pointer-events-none z-10" />

      {/* ── Content ── */}
      <div className="relative z-20 w-full max-w-screen-xl mx-auto px-8 pb-24 pt-40">
        <div className="hero-badge inline-flex items-center gap-2 mb-6">
          <span className="px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10">
            Founder &amp; Creative Director
          </span>
        </div>
        <h1 className="hero-line-1 font-heading text-white text-6xl lg:text-8xl font-black mb-4 drop-shadow-sm">
          Jaquan Little
        </h1>
        <p className="hero-sub font-body text-white/80 text-xl max-w-2xl leading-relaxed drop-shadow-sm flex flex-wrap gap-x-2">
          {['Creative Strategist.', 'Visionary.', 'Leader.'].map((word, i) => (
            <span
              key={word}
              style={{
                animation: `wordFadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both`,
                animationDelay: `${0.6 + i * 0.28}s`,
              }}
            >
              {word}
            </span>
          ))}
        </p>
      </div>

      {/* ── Scroll-down arrow ── */}
      <button
        onClick={handleScroll}
        aria-label="Scroll down"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1.5 group cursor-pointer"
        style={{ background: 'none', border: 'none' }}
      >
        <span className="font-body text-white/50 text-xs uppercase tracking-widest group-hover:text-white/80 transition-colors duration-300">
          Scroll
        </span>
        <div className="relative w-6 h-6 flex items-center justify-center">
          <i
            className="ri-arrow-down-line text-white/60 text-2xl group-hover:text-white transition-colors duration-300"
            style={{ animation: 'scrollBounce 1.6s ease-in-out infinite' }}
          />
        </div>
      </button>

      <style>{`
        @keyframes scrollBounce {
          0%, 100% { transform: translateY(0); opacity: 0.6; }
          50%       { transform: translateY(7px); opacity: 1; }
        }
        @keyframes wordFadeUp {
          0%   { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

function BioSection() {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="py-24 bg-white">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left - Portrait */}
          <div ref={leftRef} className={`scroll-hidden-left ${leftVisible ? 'scroll-visible' : ''} flex-shrink-0 w-full lg:w-[380px]`}>
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-full h-full rounded-3xl bg-gradient-to-br from-brand-blue/20 to-brand-purple/20 rounded-3xl" />
              <div className="relative h-[500px] rounded-3xl overflow-hidden border-4 border-white">
                <img
                  src="https://storage.readdy-site.link/project_files/979cb23d-2376-41a4-bad4-0dbbe3d67aab/f13eea68-c34b-4153-916f-2039c7605323__DSC3044.jpeg?v=bec5f396d731a1ffa8502590234c5072"
                  alt="Jaquan Little Portrait"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 left-5 right-5 bg-white rounded-2xl p-5 border border-brand-purple/10">
                <p className="font-heading text-brand-blue-dark text-sm font-bold italic">
                  &ldquo;Where Every Creative Mind Matters&rdquo;
                </p>
                <p className="font-body text-brand-purple text-xs mt-1">— Jaquan Little, Founder</p>
              </div>
            </div>
          </div>

          {/* Right - Bio */}
          <div ref={rightRef} className={`scroll-hidden-right ${rightVisible ? 'scroll-visible' : ''} flex-1 pt-4`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-brand-purple/10 text-brand-purple font-body text-xs font-semibold uppercase tracking-widest mb-5">
              Meet the Founder
            </span>
            <h2 className="font-heading text-5xl font-black text-brand-blue-dark mb-8 leading-tight">
              A Life Built on<br />
              <span className="gradient-text">Creative Purpose</span>
            </h2>

            <div className="space-y-5 font-body text-gray-600 text-base leading-relaxed">
              <p>
                Jaquan Little is a world-renowned worship and arts director and distinguished Creative Media Strategist, recognized for his ability to lead, develop, and elevate creatives across multiple artistic disciplines. With a career rooted in excellence, Jaquan has collaborated with countless gospel artists and creative professionals, contributing to high-level productions and live events across multiple stages and platforms.
              </p>
              <p>
                His experience spans stage plays, live productions, musical direction, and creative execution, equipping him with a well-rounded and impactful presence in the creative industry.
              </p>
              <p>
                Beyond his professional accomplishments, Jaquan is a devoted family man who leads with strong values, integrity, and principle. His commitment to faith, family, and community is at the core of everything he builds.
              </p>
              <div className="p-6 rounded-xl bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF] border-l-4 border-brand-purple">
                <p>
                  As the visionary behind Creative Institute, Jaquan is passionate about developing people, not just talent. He is especially committed to creating a safe, structured, and empowering space for all creatives, including <strong className="text-brand-purple">neurodivergent individuals</strong>, where every person is given the opportunity to grow, express themselves, and thrive.
                </p>
              </div>
              <p>
                Through this vision, Jaquan continues to build a platform that nurtures creativity, instills discipline, and inspires purpose, impacting lives far beyond the arts.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/enroll" className="whitespace-nowrap px-8 py-4 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-semibold text-base hover:opacity-90 transition-all cursor-pointer">
                Enroll Today
              </Link>
              <Link to="/contact" className="whitespace-nowrap px-8 py-4 rounded-full border-2 border-brand-purple text-brand-purple font-body font-semibold text-base hover:bg-brand-purple hover:text-white transition-all cursor-pointer">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CredentialsSection() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="py-24 bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div ref={titleRef} className="text-center mb-14">
          <span className={`scroll-hidden ${titleVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/20 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest mb-4`}>
            Credentials &amp; Experience
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVisible ? 'scroll-visible' : ''} font-heading text-5xl font-black text-brand-blue-dark`}>
            A Legacy of <span className="gradient-text">Excellence</span>
          </h2>
        </div>
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {credentials.map((cred, idx) => (
            <CredentialCard
              key={cred.title}
              {...cred}
              delay={`delay-${(idx % 3) * 100 + 100}`}
              isVisible={gridVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function VideoShowcase() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });

  const videos = [
    {
      src: "https://www.dropbox.com/scl/fi/y824cb7jyzmmfu2bzljok/Video-Mar-29-2026-6-47-27-PM.mov?rlkey=0muvm1svkh7y0m99szoyx9lt1&st=qbw5esp0&raw=1",
      label: "Creative Direction",
    },
    {
      src: "https://www.dropbox.com/scl/fi/yzuqihdlub66ow4syt5hz/Video-Mar-08-2026-9-38-30-AM.mov?rlkey=4f5mohib81opn0s0rarpmb5eq&st=obe117uz&raw=1",
      label: "In the Studio",
    },
    {
      src: "https://www.dropbox.com/scl/fi/7310wfr6q9mgj1ll33sqf/Video-Feb-20-2026-11-42-04-AM.mov?rlkey=j9nlnsg8rh40oiskrsk3phaf5&st=4yjcawuw&raw=1",
      label: "Live Performance",
    },
  ];

  return (
    <section className="py-24 bg-[#0B0F1A]">
      <div className="max-w-screen-xl mx-auto px-8">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-14">
          <span className={`scroll-hidden ${titleVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full border border-brand-yellow/40 text-brand-yellow font-body text-xs font-semibold uppercase tracking-widest mb-4`}>
            Jaquan in Action
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVisible ? 'scroll-visible' : ''} font-heading text-5xl font-black text-white`}>
            Watch the <span className="text-brand-yellow">Vision</span> Come Alive
          </h2>
        </div>

        {/* Video Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {videos.map((video, idx) => (
            <div
              key={video.label}
              className={`scroll-hidden-blur ${gridVisible ? 'scroll-visible' : ''} delay-${idx * 200 + 100} group relative rounded-2xl overflow-hidden`}
              style={{ aspectRatio: '9/16' }}
            >
              <video
                autoPlay
                muted
                loop
                playsInline
                src={video.src}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {/* Overlay label */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-brand-yellow flex-shrink-0" />
                <span className="font-body text-white/90 text-sm font-medium tracking-wide">{video.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VisionStatement() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-white">
      <div className="max-w-screen-lg mx-auto px-8 text-center">
        <span className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full border border-brand-yellow/40 text-brand-yellow font-body text-xs uppercase tracking-widest mb-8`}>
          Vision Statement
        </span>
        <p className={`scroll-hidden delay-200 ${isVisible ? 'scroll-visible' : ''} font-heading text-2xl lg:text-3xl font-light text-white leading-relaxed mb-8 italic`}>
          &ldquo;Our vision is to establish Creative Institute as a premier creative hub that transforms lives through the power of the arts — where individuals of all backgrounds and abilities are empowered to discover their voice, develop their gifts, and walk confidently in their purpose.&rdquo;
        </p>
        <div className={`scroll-hidden delay-300 ${isVisible ? 'scroll-visible' : ''} flex flex-col items-center mt-8`}>
          <div className="w-20 h-px bg-brand-yellow mb-4" />
          <p className="font-body text-brand-yellow text-sm uppercase tracking-widest">Jaquan Little, Founder &amp; Creative Director</p>
        </div>
      </div>
    </section>
  );
}

export default function FounderPage() {
  return (
    <main>
      <SEOHead
        title="Meet the Founder Jaquan Little | Creative Institute LLC NC"
        description="Meet Jaquan Little — world-renowned worship director, Creative Media Strategist, and founder of Creative Institute LLC. Bringing mobile music and arts education across North Carolina."
        keywords="Jaquan Little, Creative Institute founder, music director NC, arts education founder, creative strategist North Carolina"
        canonical="/meet-the-founder"
        jsonLd={founderJsonLd}
      />
      <HeroBlock />
      <BioSection />
      <VideoShowcase />
      <CredentialsSection />
      <VisionStatement />
    </main>
  );
}
